/// <reference path="../ledgeraccount/ReconciliationEntryState.ts" />

interface AccountingEntryCellDelegate
{
    addEntryDidPress(cell:AccountingEntryCell):void;
    amountDidUpdate(cell:AccountingEntryCell):void;
    /// The name shown for an account: the customer's or supplier's on their accounts.
    displayNameForAccount(account:LedgerAccount, accountName:string):string;
}

// A line of a journal entry: labels once the entry is locked, fields while it is a draft, and
// the new line at the bottom. The account shows its number, muted, and its name (the party's on
// a customer or supplier account); it is searched by number or name.
class AccountingEntryCell extends UITableViewCell
{
    delegate:AccountingEntryCellDelegate = null;

    private accountTextField:MUITextField = null;
    private accountTitleLabel:MUILabel = null;
    private accountLabel:MUILabel = null;

    private referenceTextField:MUITextField = null;
    private referenceLabel:MUILabel = null;
    private debitTextField:MUITextField = null;
    private debitLabel:MUILabel = null;
    private credictTextField:MUITextField = null;
    private credictLabel:MUILabel = null;

    private cf = ( MUIWebApplication.sharedInstance().delegate as AppDelegate ).currencyFormatter;

    private _account:LedgerAccount = null;
    private _account_name:string = null;
    private _account_number:string = null;
    private _reference:string = null;
    private _debit_amount:number = 0;
    private _credit_amount:number = 0;

    /// "<number> name" for a label or a dropdown, the number muted.
    static accountTitleHTML(number:string, name:string) : string {
        let escape = ReconciliationEntryStateHelper.escape;
        let title = escape(name);
        if (number == null || number.length == 0) return title;
        return "<span class=\"rec-entry-code\">" + escape(number) + "</span>" + title;
    }

    awakeFromHTML()
    {
        this.accountLabel = MUIOutlet(this, "account-lbl", "MUILabel");
        // The account field shows the chosen account (its number muted, then the name) over the
        // input. Editing shows it as text, all selected: typing searches the accounts by number
        // or name, and leaving the field without picking one shows the account again.
        this.accountTitleLabel = MUIOutlet(this, "account-title-lbl", "MUILabel");
        this.accountTextField = MUIOutlet(this, "account-tf", "MUITextField");
        if (this.accountTextField) {
            this.accountTextField.setOnBeginEditing(this, function(this:AccountingEntryCell, control:MUITextField){
                MUICoreLayerAddStyle(control.layer, "editing");
                control.selectAll(null);
            });
            this.accountTextField.setOnDidEndEditing(this, function(this:AccountingEntryCell, control:MUITextField){
                MUICoreLayerRemoveStyle(control.layer, "editing");
                this.showAccount(this._account);
            });
            this.accountTextField.setOnChangeText(this, function(this:AccountingEntryCell, control:MUITextField, value:string){
                // a line keeps its account until another is picked; the new line holds none
                if (this._item == null) {
                    this._account = null;
                    this._account_name = null;
                    this._account_number = null;
                }
                this.showSelectedLedgerAccountViewController( value.length > 0 ? value : null, true );
            });
        }

        this.referenceLabel = MUIOutlet(this, "reference-lbl", "MUILabel");
        this.referenceTextField = MUIOutlet(this, "reference-tf", "MUITextField");
        this.setupTextFieldEvents(this.referenceTextField);
        if (this.referenceTextField) this.referenceTextField.setOnChangeText(this, function(textField:MUITextField, value:string){
            this.reference = value.trim().length > 0 ? value : null;
        });

        this.debitLabel = MUIOutlet(this, "debit-lbl", "MUILabel");
        this.debitTextField = MUIOutlet(this, "debit-tf", "MUITextField");
        this.setupTextFieldEvents(this.debitTextField);
        if (this.debitTextField) {
            //this.debitTextField.formatter = this.cf;
            this.debitTextField.setOnChangeText(this, function(this:AccountingEntryCell, textField:MUITextField, value:string){
                this._debit_amount = this.cf.numberFromString(value) ?? 0;
                if (this._item == null) return; // add cell: the entry is created on add
                this._item.debitAmount = this._debit_amount;
                this.delegate?.amountDidUpdate(this);
            });
        }

        this.credictLabel = MUIOutlet(this, "credict-lbl", "MUILabel");
        this.credictTextField = MUIOutlet(this, "credict-tf", "MUITextField");
        this.setupTextFieldEvents(this.credictTextField);
        if (this.credictTextField) {
            //this.credictTextField.formatter = this.cf;
            this.credictTextField.setOnChangeText(this, function(this:AccountingEntryCell, textField:MUITextField, value:string){
                this._credit_amount = this.cf.numberFromString(value) ?? 0;
                if (this._item == null) return; // add cell: the entry is created on add
                this._item.creditAmount = this._credit_amount;
                this.delegate?.amountDidUpdate(this);
            });
        }

        // the new line's amounts show what an empty one is worth
        if (this.accountTextField != null) {
            let zero = this.cf.stringFromNumber(0);
            this.debitTextField?.setPlaceholderText(zero);
            this.credictTextField?.setPlaceholderText(zero);
        }

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private _item:AccountingEntry = null;
    set item(item:AccountingEntry)
    {
        this._item = item;
        this._account = item.account;
        this._account_name = item.account?.name;
        this._account_number = item.account?.prefix;
        this._reference = item.reference;
        this._debit_amount = item.debitAmount;
        this._credit_amount = item.creditAmount;

        let number = item.accountNumber ?? item.account?.prefix;
        let name = this.delegate?.displayNameForAccount(item.account, item.accountName) ?? item.accountName;
        this.showAccountTitle(number, name);

        if (this.referenceLabel) this.referenceLabel.text = ReconciliationEntryStateHelper.escape(item.reference);
        if (this.referenceTextField) this.referenceTextField.text = item.reference;

        // a side without amount shows a dash
        if (this.debitLabel) this.debitLabel.text = this.amountText(item.debitAmount) ?? "—";
        if (this.debitTextField) this.debitTextField.text = this.amountText(item.debitAmount);

        if (this.credictLabel) this.credictLabel.text = this.amountText(item.creditAmount) ?? "—";
        if (this.credictTextField) this.credictTextField.text = this.amountText(item.creditAmount);
    }

    private amountText(amount:number) : string {
        return amount != null && amount != 0 ? this.cf.stringFromNumber(amount) : null;
    }

    updateValues(amount:number, account:LedgerAccount)
    {
        this._account = account;
        this._account_name = account?.name;
        this._account_number = account?.prefix;
        this.showAccount(account);

        // Detect the side from the sign and the account type (same rules as AccountingEntry.setAmount)
        let debit = 0;
        let credit = 0;
        if (amount != null && amount != 0) {
            let debitType = account?.type == LedgerAccountType.debit;
            if ((amount > 0) == debitType) debit = Math.abs(amount);
            else credit = Math.abs(amount);
        }
        this.setDebitCredit(debit, credit);
    }

    updateReference(reference:string){
        this._reference = reference;
        if (this.referenceTextField) this.referenceTextField.text = reference;
    }

    // Remaining balance: a positive remainder (debit > credit) is offset with a credit, and vice versa
    set amount(amount:number){
        this.setDebitCredit(amount < 0 ? Math.abs(amount) : 0, amount > 0 ? amount : 0);
    }

    private setDebitCredit(debit:number, credit:number){
        this._debit_amount = debit;
        this._credit_amount = credit;
        if (this.debitTextField) this.debitTextField.text = this.amountText(debit);
        if (this.credictTextField) this.credictTextField.text = this.amountText(credit);
    }

    get debitAmount():number { return this._debit_amount; }
    get creditAmount():number { return this._credit_amount; }

    private showAccount(account:LedgerAccount) {
        let name = account != null ? (this.delegate?.displayNameForAccount(account, account.name) ?? account.name) : null;
        this.showAccountTitle(account?.prefix, name);
    }

    // The account's number (muted) and name in the label or over the field; the field's own text
    // is the same, plain, for when it is edited
    private showAccountTitle(number:string, name:string) {
        let hasTitle = (number?.length ?? 0) > 0 || (name?.length ?? 0) > 0;
        let title = hasTitle ? AccountingEntryCell.accountTitleHTML(number, name) : "";
        if (this.accountLabel) this.accountLabel.text = title;
        if (this.accountTitleLabel) this.accountTitleLabel.text = title;
        if (this.accountTextField) this.accountTextField.text = [number, name].filter(v => v != null && v.length > 0).join(" ");
    }

    private showSelectedLedgerAccountViewController( searchString:string, hideSearchBar:boolean = false){
        AppHelper.sharedInstance().showSelectLedgerAccountViewControllerFromView(this.accountTextField, this._account, searchString, hideSearchBar, null, this, this.updateLedgerAccount, MIOLocalizeString("NONE", "None"));
    }

    private updateLedgerAccount(controller:SelectViewController, account:LedgerAccount){
        // another picker may answer while this one was asked for (see AppHelper)
        if (account != null && !(account instanceof LedgerAccount)) return;
        // a line keeps an account; the new line may be emptied
        if (account == null && this._item != null) return;

        this.account = account;
        this.showAccount(account);
    }

    set account(account:LedgerAccount){
        this._account = account;
        this._account_name = account?.name;
        this._account_number = account?.prefix;

        this.updateAccountingEntry();
    }
    get account():LedgerAccount{ return this._account; }

    set reference(reference:string){
        this._reference = reference;
        this.updateAccountingEntry();
    }
    get reference():string{ return this._reference; }

    private updateAccountingEntry(){
        if (this._item == null) return;

        this._item.account = this._account;
        this._item.accountName = this._account_name;
        this._item.accountNumber = this._account_number;
        this._item.reference = this._reference;
        // this._item.debitAmount = this._debitAmount;
        // this._item.credictAmount = this._credictAmount
    }

    protected setupTextFieldEvents(textField:MUITextField){
        if (textField == null) return;

        textField.setOnBeginEditing(this, function(control:MUITextField, value:string){
            control.selectAll(null);
        });

        textField.setOnEnterPress(this, function(control:MUITextField){
            control.resignFirstResponder();
            this.enterDidPress()
        });
    }

    // Enter adds the new line; on a line of the entry it only ends the editing
    private enterDidPress(){
        if (this._item != null) return;
        this.delegate?.addEntryDidPress(this);
    }

}
